import { cloneDeep } from 'lodash';

import { SportId } from '@core/constants';
import { MetaData } from '@core/models/offer/MetaData';

export type LiveData = {
    score: PeriodEntry[];
    serve?: number;
    stats?: any[];
};

export type RawLiveData = {
    periods: RawLiveDataPeriods[];
    team1Score: number | undefined;
    team2Score: number | undefined;
    team1GemScore?: number;
    team2GemScore?: number;
    serve?: number;
};

export enum CompetitorNumber {
    firstCompetitor = 1,
    secondCompetitor = 2,
}

export function getLiveData(sportId: SportId, rawLiveData: RawLiveData, useCompactLiveData: boolean, isFinished = false): LiveData {
    let liveDataGetter: LiveDataGetter | undefined;
    if (isFinished) {
        liveDataGetter = getCurrentScore;
    } else {
        liveDataGetter = liveDataGetterMapping[sportId];
        if (!liveDataGetter) {
            liveDataGetter = getRestData;
        }
    }
    let liveData = liveDataGetter(rawLiveData, useCompactLiveData);
    if (sportId in penaltyPeriodIndices) {
        liveData = addBracketsToPenaltyPeriod(liveData, sportId);
    }
    return liveData;
}

const liveDataGetterMapping: Partial<Record<SportId, LiveDataGetter>> = {
    [SportId.Tennis]: getTennisData,
    [SportId.Football]: getFootballData,
    [SportId.Volleyball]: getServeSportData,
    [SportId.TableTennis]: getServeSportData,
    [SportId.BeachVolleyball]: getServeSportData,
    [SportId.Badminton]: getServeSportData,
    [SportId.Basketball]: getPerPeriodSportData,
    [SportId.Handball]: getPerPeriodSportData,
    [SportId.IceHockey]: getPerPeriodSportData,
    [SportId.Rugby]: getPerPeriodSportData,
};

const penaltyPeriodIndices: Partial<Record<SportId, number>> = {
    [SportId.Football]: 3,
    [SportId.Handball]: 3,
    [SportId.IceHockey]: 4,
    [SportId.Rugby]: 5,
};

function addBracketsToPenaltyPeriod(liveData: LiveData, sportId: SportId): LiveData {
    let liveDataWithBrackets = cloneDeep(liveData);
    const penaltyPeriodIndex = penaltyPeriodIndices[sportId]!;
    if (liveData.score.length > penaltyPeriodIndex) {
        const penaltyPeriod = liveDataWithBrackets.score[penaltyPeriodIndex];
        penaltyPeriod[0] = `(${penaltyPeriod[0]})`;
        penaltyPeriod[1] = `(${penaltyPeriod[1]})`;
    }
    return liveDataWithBrackets;
}

function getFootballData(liveData: RawLiveData, _useCompactLiveData: boolean): LiveData {
    const data: LiveData = {
        serve: undefined,
        score: [],
    };
    liveData.periods.forEach((period, index) => {
        // do not accumulate for first period or for penalties which have index 4
        if (index === 0 || index === 4) {
            data.score.push([period.team1Score, period.team2Score]);
        } else {
            // add second extra time to first extra time
            if (index === 3) {
                data.score[index - 1] = [
                    Number(period.team1Score) + Number(data.score[index - 1][0]),
                    Number(period.team2Score) + Number(data.score[index - 1][1]),
                ];
            } else {
                data.score.push([
                    Number(period.team1Score) + Number(data.score[index - 1][0]),
                    Number(period.team2Score) + Number(data.score[index - 1][1]),
                ]);
            }
        }
    });
    return data;
}

function getServeSportData(rawLiveData: RawLiveData, _useCompactLiveData: boolean): LiveData {
    return {
        score: rawLiveData.periods.map((period) => ([period.team1Score, period.team2Score])),
        serve: rawLiveData.serve,
    };
}

function getTennisData(rawLiveData: RawLiveData, useCompactLiveData: boolean): LiveData {
    let lastSet: PeriodEntry;
    if (rawLiveData.periods.length === 0) {
        lastSet = emptyPeriod;
    } else {
        const lastPeriodScore = rawLiveData.periods[rawLiveData.periods.length - 1];
        lastSet = [lastPeriodScore.team1Score, lastPeriodScore.team2Score];
    }
    let gemScore: PeriodEntry = [0, 0];
    if (rawLiveData.team1GemScore || rawLiveData.team1GemScore === 0) {
        gemScore = [rawLiveData.team1GemScore, rawLiveData.team2GemScore!];
    }

    let score: PeriodEntry[];
    if (useCompactLiveData) {
        score = [
            gemScore,
            lastSet,
        ];
        if (rawLiveData.team1Score !== undefined && rawLiveData.team2Score !== undefined) {
            score.push([rawLiveData.team1Score, rawLiveData.team2Score]);
        }
    } else {
        score = [
            gemScore,
            ...rawLiveData.periods.map((p: RawLiveDataPeriods) => [p.team1Score, p.team2Score] as [number, number]),
        ];
    }

    return {
        score,
        serve: rawLiveData.serve,
    };
}

function getPerPeriodSportData(liveData: RawLiveData, useCompactLiveData: boolean): LiveData {
    if (useCompactLiveData) {
        return getCurrentScore(liveData);
    } else {
        const data: LiveData = {
            serve: undefined,
            score: [],
        };
        data.score = liveData.periods.map((period) => ([period.team1Score, period.team2Score]));
        if (liveData.team1Score !== undefined && liveData.team2Score !== undefined) {
            data.score.push([liveData.team1Score, liveData.team2Score]);
        }
        // if there is only one period remove overall score
        if (data.score.length === 2) {
            data.score = data.score.slice(0, 1);
        }
        return data;
    }
}

function getRestData(liveData: RawLiveData, useCompactLiveData: boolean): LiveData {
    const currentScore = getCurrentScore(liveData);
    if (!useCompactLiveData
        && (liveData.periods.length > 1
            || isCurrentScoreDifferentFromPeriodScore(liveData))) {
        const lastPeriod = liveData.periods[liveData.periods.length - 1];
        currentScore.score.unshift([lastPeriod.team1Score, lastPeriod.team2Score]);
    }
    return currentScore;
}

function isCurrentScoreDifferentFromPeriodScore(liveData: RawLiveData): boolean {
    if (liveData.periods.length === 0) {
        return false;
    }
    const lastPeriod = liveData.periods[liveData.periods.length - 1];
    return lastPeriod.team1Score !== liveData.team1Score
        || lastPeriod.team2Score !== liveData.team2Score;
}

function getCurrentScore(rawLiveData: RawLiveData): LiveData {
    const score: PeriodEntry[] = [];
    if (rawLiveData.team1Score !== undefined && rawLiveData.team2Score !== undefined) {
        score.push([rawLiveData.team1Score, rawLiveData.team2Score]);
    }
    return {
        score,
        serve: rawLiveData.serve,
    };
}

export function extractTennisResult(liveData: any): string {
    const result = getTennisData(liveData, false).score.map((scorePair) => `${scorePair[0]}:${scorePair[1]}`);

    return result.join(' ');
}

const emptyPeriod: PeriodEntry = ['-', '-'];

type PeriodEntry = [number | string, number | string];

type RawLiveDataPeriods = {
    team1Score: number,
    team2Score: number,
};

type LiveDataGetter = (liveData: any, useCompactLiveData: boolean) => LiveData;

export function getCurrentPeriodTranslation(metaData: MetaData): {
    key: string,
    period?: number,
} | null {
    if (!metaData.matchStatusLabel) {
        return null;
    }
    const periodLabel = metaData.matchStatusLabel;
    const key = getPeriodTranslationKey(periodLabel);
    if (key) {
        return {
            key,
            period: parseInt(periodLabel.charAt(0), 10),
        };
    } else if (periodLabel in periodMap) {
        return {
            key: periodMap[periodLabel],
        };
    } else {
        return null;
    }
}

function getPeriodTranslationKey(periodLabel: string): string | undefined {
    if (periodLabel.length !== 2) {
        return undefined;
    }
    if (periodLabel.endsWith('S')) {
        return 'sportOffer.periods.set';
    }
    if (periodLabel.endsWith('P')) {
        return 'sportOffer.periods.period';
    }
    if (periodLabel.endsWith('Q')) {
        return 'sportOffer.periods.quarter';
    }
    if (periodLabel.endsWith('I')) {
        return 'sportOffer.periods.inning';
    }
}

const periodMap: { [key: string]: string } = {
    P: 'sportOffer.periods.penalties',
    AP: 'sportOffer.periods.awaiting_penalties',
    '1E': 'sportOffer.periods.first_extra_half',
    '2E': 'sportOffer.periods.second_extra_half',
    EHT: 'sportOffer.periods.extra_halftime',
    WET: 'sportOffer.periods.awaiting_extra_time',
    HT: 'sportOffer.periods.halftime',
    '1H': 'sportOffer.periods.first_half',
    '2H': 'sportOffer.periods.second_half',
};
